import {
  ChallengeDifficultyBadge,
  ChallengeTypeBadge,
  MaxCompletionsBadge,
  SpeedBonusBadge,
  type ChallengeType,
  type ChallengeDifficulty,
} from '@/components/challenges';
import type { TemplateChallengeGroup, TemplateChallengeLink } from '@/config/objects/templates';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  GripVertical,
  Layers,
  MoreHorizontal,
  Settings,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface UngroupedChallengesListProps {
  templateId: string;
  challenges: TemplateChallengeLink[];
  groups: TemplateChallengeGroup[];
}

export function UngroupedChallengesList({
  templateId,
  challenges,
  groups,
}: UngroupedChallengesListProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<TemplateChallengeLink | null>(null);

  const removeMutation = useMutation({
    mutationFn: async (linkId: string) => {
      return apiClient.delete(`/templates/${templateId}/challenges/${linkId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TemplateChallenges', templateId] });
      queryClient.invalidateQueries({ queryKey: ['TemplateGroups', templateId] });
      queryClient.invalidateQueries({ queryKey: ['Template', templateId] });
      toast.success('Challenge removed from template');
      setDeleteTarget(null);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to remove challenge');
    },
  });

  const moveToGroupMutation = useMutation({
    mutationFn: async ({ linkId, groupId }: { linkId: string; groupId: string }) => {
      return apiClient.patch(`/templates/${templateId}/challenges/${linkId}`, {
        groupId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TemplateChallenges', templateId] });
      queryClient.invalidateQueries({ queryKey: ['TemplateGroups', templateId] });
      toast.success('Challenge moved to group');
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to move challenge');
    },
  });

  const handleMoveToGroup = (linkId: string, groupId: string) => {
    moveToGroupMutation.mutate({ linkId, groupId });
  };

  return (
    <>
      <div className="pt-4 border-t mt-6">
        <Card className="rounded-xl border-amber-200 bg-amber-50/50 shadow-sm">
          <CardHeader className="px-4 py-3 border-b border-amber-200">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <h3 className="font-semibold text-amber-900">
                  Ungrouped Challenges ({challenges.length})
                </h3>
                <p className="text-sm text-amber-700/80">
                  These challenges need to be assigned to a group
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <ul className="divide-y divide-amber-200">
              {challenges.map((link) => (
                <li
                  key={link.id}
                  className="flex items-center gap-3 p-3 hover:bg-amber-100/50 transition-colors group"
                >
                  <GripVertical className="h-4 w-4 text-amber-400 opacity-0 group-hover:opacity-100" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/challenges/${link.libraryChallenge.id}`}
                        className="font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {link.libraryChallenge.name}
                      </Link>
                      {link.maxCompletions != null && (
                        <MaxCompletionsBadge maxCompletions={link.maxCompletions} />
                      )}
                      <SpeedBonusBadge speedBonuses={link.speedBonuses} />
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <ChallengeTypeBadge type={link.libraryChallenge.type as ChallengeType} />
                      <ChallengeDifficultyBadge difficulty={link.libraryChallenge.difficulty as ChallengeDifficulty} />
                      <span className="text-xs text-muted-foreground">
                        {link.libraryChallenge.points} pts
                      </span>
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/templates/${templateId}/challenges/${link.id}/settings`)}>
                        <Settings className="h-4 w-4 mr-2" />
                        Challenge Settings
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {groups.length > 0 && (
                        <>
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              <ArrowRight className="h-4 w-4 mr-2" />
                              Move to Group
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              {groups.map((group) => (
                                <DropdownMenuItem
                                  key={group.id}
                                  onClick={() => handleMoveToGroup(link.id, group.id)}
                                >
                                  <Layers className="h-4 w-4 mr-2" />
                                  {group.name}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      <DropdownMenuItem
                        onClick={() => setDeleteTarget(link)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove from Template
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Remove Challenge"
        description={`Are you sure you want to remove "${deleteTarget?.libraryChallenge.name}" from this template?`}
        confirmText="Remove"
        variant="destructive"
        loading={removeMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) removeMutation.mutate(deleteTarget.id);
        }}
      />

    </>
  );
}
